import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Upload, FileText, Trash2, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, X, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LegalBanner() {
  const { t } = useTranslation();
  return (
    <div className="bg-slate-800 text-slate-300 text-xs py-2 px-4 text-center" data-testid="legal-banner">
      <Info className="inline h-3 w-3 mr-1.5 -mt-0.5" />
      {t("legal.banner")}
    </div>
  );
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [selectedForm, setSelectedForm] = useState("");
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [docsRes, formsRes] = await Promise.all([
          axios.get(`${API}/documents`, { withCredentials: true }),
          axios.get(`${API}/forms`),
        ]);
        setDocuments(docsRes.data);
        setForms(formsRes.data);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleUpload = async (files) => {
    if (!selectedForm) { toast.error(t("pages.selectFormFirst")); return; }
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("form_id", selectedForm);
      formData.append("doc_type", file.name.split(".").pop());
      try {
        const res = await axios.post(`${API}/documents/upload`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDocuments((prev) => [res.data, ...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    toast.success("Documents uploaded");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleUpload(Array.from(e.dataTransfer.files));
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, { withCredentials: true });
      setDocuments((prev) => prev.filter((d) => d.doc_id !== docId));
      toast.success("Document deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleValidate = async () => {
    if (!selectedForm) { toast.error(t("pages.selectFormFirst")); return; }
    setValidating(true);
    try {
      const res = await axios.post(
        `${API}/documents/validate`,
        { form_id: selectedForm },
        { withCredentials: true }
      );
      setValidation(res.data.validation);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t("pages.proFeature"));
      } else {
        toast.error("Validation failed");
      }
    }
    setValidating(false);
  };

  const formDocs = selectedForm ? documents.filter((d) => d.form_id === selectedForm) : documents;
  const isPremium = user?.plan === "pro";

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="documents-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="documents-page">
      <LegalBanner />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.documentsTitle")}</h1>
            <p className="text-base text-slate-500">{t("pages.documentsSubtitle")}</p>
          </div>
        </div>

        {/* Form selector */}
        <div className="mb-6">
          <Select value={selectedForm} onValueChange={setSelectedForm}>
            <SelectTrigger className="w-full max-w-sm" data-testid="doc-form-select">
              <SelectValue placeholder={t("pages.docFormLabel")} />
            </SelectTrigger>
            <SelectContent>
              {forms.map((f) => (
                <SelectItem key={f.form_id} value={f.form_id}>{f.code} - {f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 mb-8 text-center transition-colors duration-200 ${
            dragActive ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white hover:border-teal-300"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          data-testid="upload-drop-zone"
        >
          <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700 mb-1">
            {t("pages.dragDrop")}
            <label className="text-teal-700 cursor-pointer hover:underline ml-1">
              browse
              <input
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => handleUpload(Array.from(e.target.files))}
                data-testid="file-input"
              />
            </label>
          </p>
          <p className="text-xs text-slate-400">{t("pages.maxFileSize")}</p>
          {uploading && <Loader2 className="h-5 w-5 text-teal-600 animate-spin mx-auto mt-3" />}
        </div>

        {/* Validate button */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            onClick={handleValidate}
            disabled={validating || !selectedForm}
            className={isPremium ? "bg-teal-700 hover:bg-teal-800 text-white" : "bg-slate-200 text-slate-600"}
            data-testid="validate-docs-btn"
          >
            {validating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : isPremium ? <ShieldCheck className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            {isPremium ? t("pages.validateDocs") : `${t("pages.validateDocs")} (${t("pages.proFeature")})`}
          </Button>
          {!isPremium && (
            <span className="text-xs text-slate-500">{t("pages.validateForForm")}</span>
          )}
        </div>

        {/* Validation results */}
        {validation && (
          <Card className="border-teal-200 bg-teal-50/30 mb-8" data-testid="validation-results">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-teal-700" />
                <h3 className="text-base font-semibold text-slate-900">{t("pages.aiValidation")}</h3>
              </div>
              <div className="space-y-3">
                {validation.coverage && (
                  <p className="text-sm text-slate-700">Coverage: <strong>{validation.coverage}</strong></p>
                )}
                {validation.missing?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-1">Missing Documents:</p>
                    <ul className="space-y-1">
                      {validation.missing.map((m, i) => (
                        <li key={i} className="text-sm text-red-600 flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {m}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {validation.warnings?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-amber-700 mb-1">Warnings:</p>
                    <ul className="space-y-1">
                      {validation.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-amber-600 flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {validation.recommendations?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-teal-700 mb-1">Recommendations:</p>
                    <ul className="space-y-1">
                      {validation.recommendations.map((r, i) => (
                        <li key={i} className="text-sm text-teal-600 flex items-start gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document list */}
        {formDocs.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200" data-testid="docs-empty">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{t("pages.noDocs")}</h3>
              <p className="text-sm text-slate-500">{t("pages.noDocsDesc")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {formDocs.map((doc) => (
              <Card key={doc.doc_id} className="border-slate-100" data-testid={`doc-${doc.doc_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-slate-50 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{doc.filename}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                        <span>{formatSize(doc.file_size)}</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">{doc.status}</Badge>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDelete(doc.doc_id)}
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`delete-doc-${doc.doc_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
